import { useEffect, useState, type RefObject } from 'react';

/**
 * How tall a region has to be to reach the bottom of the screen.
 *
 * The assistant is the one screen in the product that is not a document. A page
 * that scrolls puts the box you type into below everything you have already
 * asked — on a phone, six turns in, the composer sat off the bottom of a
 * 1,200px page and the only way to ask a second question was to scroll past the
 * answer to the first. A conversation scrolls; the page it sits in does not.
 *
 * Only the distance from the top of the document is measured here. What is
 * reserved underneath — the bottom bar and the home indicator on a phone, the
 * page's own padding on a desktop — is `--chat-reserve` in the stylesheet,
 * because it is chrome and `env()` has no meaning in JavaScript.
 *
 * Measuring the top is safe to repeat: it is decided by what sits above this
 * element, never by the height we hand back, so there is no loop to fall into.
 */
export function useFillHeight(ref: RefObject<HTMLElement | null>): string | undefined {
  const [top, setTop] = useState<number | null>(null);

  useEffect(() => {
    const measure = () => {
      const el = ref.current;
      if (!el) return;
      // Document-relative, so a page that happens to be scrolled when this runs
      // does not report a smaller region than there is.
      setTop(el.getBoundingClientRect().top + window.scrollY);
    };
    measure();
    // Again once the real faces have loaded. Everything above this element —
    // the page title, its lead, the verify notice — is type, and measuring
    // against the fallback metrics left the region 25px too tall, which is a
    // page that scrolls by exactly the amount nobody can explain.
    void document.fonts?.ready.then(measure);
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [ref]);

  return top === null ? undefined : `calc(100dvh - ${Math.round(top)}px - var(--chat-reserve))`;
}
